import clsx from 'clsx';
import { BackspaceIcon } from '@/components/icons/svg-icons';
import { useBetslipStore } from '@/store/betslip';
import { addCommasWithFix } from '@/utils/odds-utils';
import { bettingInputMaxLength, useTicketBet } from '@/hooks/query';
import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { betslipStyle } from '@/features/betslip/betslip.style';

interface StakePerBetProp {
	isParlay: boolean
}

export const StakePerBet = ({ isParlay }: StakePerBetProp) => {

	const key = isParlay ? 'ParlayStakePerBet' : 'MultiStakePerBet';

	const input = useRef<HTMLInputElement>(null);

	const t = useTranslations('General');
	const { oddsType, quickStake, isIndependentMode } = useBetslipContext();

	const isUseDefaultQuickStake = quickStake?.startsWith('1');

	const [
		currentMultiTickets,
		currentParlayTicketModel,
		focusOddsKey, 
		setFocusOddsKey, 
		selectAllOddsKey,
		setSelectAllOddsKey,
		updateTicket, 
		multiStakePerBet,
		parlayStakePerBet,
		setMultiStakePerBet,
		setParlayStakePerBet,
		checkStake
	] = useBetslipStore(state => [
		state.currentMultiTickets,
		state.currentParlayTicketModel,
		state.focusOddsKey, 
		state.setFocusOddsKey, 
		state.selectAllOddsKey,
		state.setSelectAllOddsKey,
		state.updateTicket, 
		state.multiStakePerBet,
		state.parlayStakePerBet,
		state.setMultiStakePerBet,
		state.setParlayStakePerBet,
		state.checkStake
	]);

	const stake = isParlay ? parlayStakePerBet : multiStakePerBet;

	const { refetch, isFetching } = useTicketBet();

	const isSelectAll = selectAllOddsKey === key;

	const updateAllTickets = (newStake: number) => {
		if (isParlay) {
			const comboParlays = currentParlayTicketModel?.comboData.filter(parlayTicket => parlayTicket.isComboParlay);
			
			comboParlays?.forEach(parlayTicket => {
				const newTicket = {
					...parlayTicket,
					stake: newStake
				};
				updateTicket(newTicket, true, oddsType);
			});
		} else {
			currentMultiTickets.forEach(ticket => {
				const newTicket = {
					...ticket,
					stake: newStake
				};
				updateTicket(newTicket, false, oddsType);
			});
		}

		setStake(newStake);
	}

	const setStake = (newStake: number) => {
		if (isParlay) {
			setParlayStakePerBet(newStake);
		} else {
			setMultiStakePerBet(newStake);
		}
	}

	const onFocus = () => {
		input.current?.select();
		setFocusOddsKey(key);
		if (stake) {
			setSelectAllOddsKey(key);
		} else {
			setSelectAllOddsKey('');
		}
	}

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const input = e.target.value;
		const newStake = parseInt(input.toString().replace(/[^0-9]/g, '')) || 0;

		setSelectAllOddsKey('');
		updateAllTickets(newStake);
	}

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const code = e.code;
		const isAlphaPressed = code.startsWith('Key');

		if (isAlphaPressed && !e.ctrlKey) {
			e.preventDefault();
			return;
		}

		if (code === 'Enter' || code === 'NumpadEnter') {
			if (checkStake(isIndependentMode, oddsType) && !isFetching) {
				refetch();
			}
			return;
		}

		const stakeLength = stake.toString().length;

		if (stakeLength >= bettingInputMaxLength) {
			setStake(0);
		}
	}

	const plusStake = (setting: string) => {
		const value = Number(setting);

		let newStake = stake + value;
    
		if (isSelectAll) {
			newStake = value;
		}

		setSelectAllOddsKey('');
		updateAllTickets(newStake);
	}

	const onNumberPress = (value: string) => {
		let newStake = stake.toString();

		if (newStake.length >= bettingInputMaxLength || isSelectAll) {
			newStake = value;
		} else {
			newStake = newStake + value;
		}
    
		setSelectAllOddsKey('');
		updateAllTickets(Number(newStake));
	}

	const onBackspacePress = () => {
		let newStake = stake.toString();

		if (isSelectAll) {
			newStake = '0';
		} else {
			newStake = newStake.substring(0, newStake.length - 1);
		}

		setSelectAllOddsKey('');
		updateAllTickets(Number(newStake));
	}

	const onDonePress = () => {
		setFocusOddsKey('');
		setSelectAllOddsKey('');
	}

	// 從所有票中取最大的minBet作為stake per bet的minBet
	const minBet = isParlay ? 
		Number(currentParlayTicketModel?.MinBet) :
		Math.max(...(currentMultiTickets.map(ticket => ticket.minBet)));
    
	// 從所有票中取最小的maxBet作為stake per bet的maxBet
	const maxBet = isParlay ? 
		Math.min(...(currentParlayTicketModel?.comboData.map(parlayTicket => parlayTicket.MaxBet) || [])):
		Math.min(...(currentMultiTickets.map(ticket => ticket.maxBet)));

	const formatBetValue = (value: number) => {
		if (value >= 1000) {
			return `${(value / 1000).toString()}K`;
		}
		return value.toString();
	};
	
	// 如果產生minBet, maxBet衝突狀況, 則單純顯示stake作為place holder
	const placeHolder = minBet <= maxBet ? `${formatBetValue(minBet)}-${formatBetValue(maxBet)}` : 'Stake';
	

	let quickStakeSettings = new Array<string>();

	if (isUseDefaultQuickStake) {
		if (minBet > maxBet) {
			quickStakeSettings = ['1', '10', '100'];
		} else {
			quickStakeSettings = [
				(minBet * 2).toString(), 
				(minBet * 5).toString(), 
				(minBet * 10).toString()
			];
		}
	} else {
		quickStakeSettings = quickStake.split(':').
			filter((value, index) => index != 0).
			reverse();
	}

	return (
		<div className={clsx('bet-slip-parlay', isParlay ? betslipStyle.betSlipParlay : betslipStyle.betSlipSingle)}>
			<div className="bet-slip-parlay__wrap">
				<div className="bet-slip-parlay__container">
					<div className="bet-slip-parlay__header">
						<div className="bet-slip-parlay__name">{t('lbl_StakePerBet')}</div>
					</div>
				</div>
				<div className="bet-slip-parlay__stake">
					<div
						className={clsx('stake-input', 'stake-input--small', betslipStyle.betInput)}
						data-focus={focusOddsKey === key}
						data-selected={isSelectAll}
						onClick={onFocus}
					>
						<div className="stake-input__stake">
							<div className="stake-input__textfield">
								<div
									className="stake-input__input"
									data-placeholder={placeHolder}
									data-show-placeholder={!stake}
								>
									{stake === 0 ? '' : addCommasWithFix(stake)}
								</div>
								<input
									ref={input}
									className="input"
									placeholder={placeHolder}
									type="input"
									value={stake === 0 ? '' : addCommasWithFix(stake)}
									onFocus={onFocus}
									onChange={onChange}
									onKeyDown={onKeyDown}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="bet-slip-parlay__keypad" data-show={focusOddsKey === key}>
				<div className="quick-stake">
					{
						//settings為字串陣列, 沒有可供識別為key的property, 且每次更新不會有相同的dom可保存, 因此才用index當key
						quickStakeSettings?.map((setting, index) => {
							return (
								<button key={index} className="btn" onClick={() => plusStake(setting)}>
									<span className="btn__text">+{setting}</span>
								</button>
							)
						})
					}
					<button className="btn" onClick={() => updateAllTickets(maxBet)}><span className="btn__text">{t('lbl_maxbet')}</span></button>
				</div>
				<div className="keypad keypad--quickstake-mini">
					<div className="keypad__numbers">
						<div className="keypad-btn" onClick={() => onNumberPress('1')}>1</div>
						<div className="keypad-btn" onClick={() => onNumberPress('2')}>2</div>
						<div className="keypad-btn" onClick={() => onNumberPress('3')}>3</div>
						<div className="keypad-btn" onClick={() => onNumberPress('4')}>4</div>
						<div className="keypad-btn" onClick={() => onNumberPress('5')}>5</div>
						<div className="keypad-btn" onClick={() => onNumberPress('6')}>6</div>
						<div className="keypad-btn" onClick={() => onNumberPress('7')}>7</div>
						<div className="keypad-btn" onClick={() => onNumberPress('8')}>8</div>
						<div className="keypad-btn" onClick={() => onNumberPress('9')}>9</div>
						<div className="keypad-btn" onClick={() => onNumberPress('0')}>0</div>
						<div className="keypad-btn" onClick={() => onNumberPress('00')}>00</div>
						<div className="keypad__backspace" onClick={onBackspacePress}>
							<div className="keypad-btn">
								<BackspaceIcon />
							</div>
						</div>
					</div>
					<div className="keypad__quickstake">
						{
							//settings為字串陣列, 沒有可供識別為key的property, 且每次更新不會有相同的dom可保存, 因此才用index當key
							quickStakeSettings?.map((setting, index) => {
								return <div key={index} className="keypad-btn" onClick={() => plusStake(setting)}>+{setting}</div>
							})
						}
					</div>
					<div className="keypad__submit" onClick={onDonePress}>
						<div className="keypad-btn">{t('lbl_Done')}</div>
					</div>
				</div>
			</div>
		</div>
	);
};